import { RowButton } from "@/components/ui/pressable";
import type { ReactNode } from "react";
import { useState } from "react";
import {
  MoreHorizontal,
  MessageSquare,
  Pencil,
  Star,
  Trash,
  UserRound,
} from "lucide-react";
import {
  useDelete,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
  type Identifier,
} from "ra-core";

import { IconButton } from "@/components/ui/icon-button";
import { Badge } from "@/components/ui/badge";
import { Confirm } from "@/components/admin/confirm";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableHead } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar } from "@/components/atomic-crm/contacts/Avatar";
import { Status } from "@/components/atomic-crm/misc/Status";
import type { Company, Contact } from "@/components/atomic-crm/types";
import { ContactFormDialog } from "@/modules/contacts/ContactFormDialog";
import { ContactShowSheet } from "@/modules/clients/ContactShowSheet";
import type { CompanyWithPrimaryContact } from "@/modules/clients/clientProfile";
import {
  getContactEmail,
  getContactFullName,
  getContactPhoneRaw,
} from "@/modules/clients/clientShowUtils";
import { CrmPhoneDisplay } from "@/modules/voice/CrmPhoneDisplay";
import { contactHasSmsPhone } from "@/modules/messages/messageContactUtils";
import { useMessagesQuickAccess } from "@/modules/messages/MessagesQuickAccessProvider";
import { useMessagingEnabled } from "@/modules/messages/useMessagingEnabled";
import {
  CONTACT_STATUS_FILTER,
  LEAD_STATUS_FILTER,
} from "@/modules/shared/relatedFilters";
import { DataGridTable, type GridColumn } from "@/modules/shared/dataGrid";

type ClientContactsTabProps = {
  companyId: Company["id"];
  primaryContactId?: CompanyWithPrimaryContact["primary_contact_id"];
  statusFilter?: "all" | "contacts" | "leads";
};

export const ClientContactsTab = ({
  companyId,
  primaryContactId,
  statusFilter = "all",
}: ClientContactsTabProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isPending: isUpdatingPrimary }] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete<Contact>();
  const statusInFilter =
    statusFilter === "leads"
      ? LEAD_STATUS_FILTER
      : statusFilter === "contacts"
        ? CONTACT_STATUS_FILTER
        : undefined;

  const { data: contacts = [], isPending } = useGetList<Contact>(
    "contacts",
    {
      filter: {
        "company_id@eq": companyId,
        ...(statusInFilter ? { "status@in": statusInFilter } : {}),
      },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "last_name", order: "ASC" },
    },
    { staleTime: 30_000 },
  );
  const [selectedContactId, setSelectedContactId] = useState<Identifier | null>(
    null,
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editContactId, setEditContactId] = useState<Identifier | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
  const { smsEnabled } = useMessagingEnabled();
  const { openSms, isOpening: isOpeningSms } = useMessagesQuickAccess();

  const openContact = (contactId: Identifier) => {
    setSelectedContactId(contactId);
    setSheetOpen(true);
  };

  const openEdit = (contactId: Identifier) => {
    setEditContactId(contactId);
    setEditOpen(true);
  };

  const setPrimaryContact = (contactId: Identifier) => {
    update(
      "companies",
      {
        id: companyId,
        data: { primary_contact_id: contactId },
        previousData: { id: companyId, primary_contact_id: primaryContactId },
      },
      {
        onSuccess: () => {
          notify("Primary contact updated");
          refresh();
        },
        onError: () => {
          notify("Failed to update primary contact", { type: "error" });
        },
      },
    );
  };

  const deleteContact = () => {
    if (!deleteTarget) return;
    deleteOne(
      "contacts",
      { id: deleteTarget.id, previousData: deleteTarget },
      {
        onSuccess: () => {
          notify("Contact deleted", { type: "info" });
          refresh();
          setDeleteTarget(null);
        },
        onError: () => {
          notify("Failed to delete contact", { type: "error" });
        },
      },
    );
  };

  const contactColumns: GridColumn<Contact>[] = [
    {
      id: "name",
      label: "Name",
      type: "text",
      sortable: true,
      filterable: true,
      alwaysVisible: true,
      value: (contact) => getContactFullName(contact),
      cell: (contact) => (
        <RowButton
          layout="none"
          density="none"
          className="flex min-w-0 items-center gap-2 text-left hover:underline"
          onClick={() => openContact(contact.id)}
        >
          <Avatar record={contact} width={25} />
          <span className="font-medium">{getContactFullName(contact)}</span>
        </RowButton>
      ),
    },
    {
      id: "phone",
      label: "Phone",
      type: "text",
      filterable: true,
      value: (contact) => getContactPhoneRaw(contact),
      cellClassName: "text-muted-foreground",
      cell: (contact) => (
        <CrmPhoneDisplay
          phone={getContactPhoneRaw(contact)}
          contactId={contact.id}
        />
      ),
    },
    {
      id: "email",
      label: "Email",
      type: "text",
      filterable: true,
      value: (contact) => getContactEmail(contact),
      cellClassName: "text-muted-foreground",
      cell: (contact) => (
        <span className="break-all">{getContactEmail(contact)}</span>
      ),
    },
    {
      id: "status",
      label: "Status",
      type: "text",
      sortable: true,
      filterable: true,
      cell: (contact) =>
        contact.status ? <Status status={contact.status} /> : "—",
    },
    {
      id: "primary",
      label: "Primary",
      type: "boolean",
      filterable: true,
      value: (contact) => String(contact.id) === String(primaryContactId),
      headClassName: "w-[100px]",
      cell: (contact) =>
        String(contact.id) === String(primaryContactId) ? (
          <Badge variant="secondary">Primary</Badge>
        ) : (
          "—"
        ),
    },
    {
      id: "title",
      label: "Title",
      type: "text",
      sortable: true,
      filterable: true,
      defaultHidden: true,
      cellClassName: "text-muted-foreground",
      cell: (contact) => contact.title || "—",
    },
  ];

  if (isPending) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <>
      {contacts.length === 0 ? (
        <ClientTabEmpty message="No contacts linked to this client. Use the + button above to add one." />
      ) : (
        <DataGridTable
          tableId="client.contacts"
          columns={contactColumns}
          rows={contacts}
          rowKey={(contact) => contact.id}
          trailingHead={
            <TableHead className="w-[72px] text-right">Actions</TableHead>
          }
          trailingCell={(contact) => {
            const isPrimary = String(contact.id) === String(primaryContactId);
            return (
              <div className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton aria-label="Contact actions">
                      <MoreHorizontal className="size-4" />
                      <span className="sr-only">Contact actions</span>
                    </IconButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openContact(contact.id)}>
                      <UserRound className="size-4" />
                      View
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openEdit(contact.id)}>
                      <Pencil className="size-4" />
                      Edit
                    </DropdownMenuItem>
                    {smsEnabled && contactHasSmsPhone(contact) ? (
                      <DropdownMenuItem
                        disabled={isOpeningSms}
                        onClick={() => void openSms(contact)}
                      >
                        <MessageSquare className="size-4" />
                        Send SMS
                      </DropdownMenuItem>
                    ) : null}
                    {!isPrimary ? (
                      <DropdownMenuItem
                        disabled={isUpdatingPrimary}
                        onClick={() => setPrimaryContact(contact.id)}
                      >
                        <Star className="size-4" />
                        Set as primary
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem
                      variant="destructive"
                      disabled={isDeleting}
                      onClick={() => setDeleteTarget(contact)}
                    >
                      <Trash className="size-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          }}
        />
      )}

      <ContactShowSheet
        contactId={selectedContactId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
      {editContactId ? (
        <ContactFormDialog
          contactId={editContactId}
          open={editOpen}
          onOpenChange={(open) => {
            setEditOpen(open);
            if (!open) setEditContactId(null);
          }}
        />
      ) : null}
      <Confirm
        isOpen={!!deleteTarget}
        title="Delete this contact?"
        content="This action cannot be undone."
        confirm="Delete"
        confirmColor="warning"
        onConfirm={deleteContact}
        onClose={() => setDeleteTarget(null)}
        loading={isDeleting}
      />
    </>
  );
};

export const ClientTabEmpty = ({
  message,
  action,
}: {
  message: string;
  action?: ReactNode;
}) => (
  <div className="flex flex-col items-center gap-3 py-10 text-center">
    <p className="text-sm text-muted-foreground">{message}</p>
    {action}
  </div>
);
